import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
    withJsonpSupport,
    withXhr
} from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeAT from '@angular/common/locales/de-AT';
import localeDeCH from '@angular/common/locales/de-CH';
import localeEn from '@angular/common/locales/en';
import localeEnGB from '@angular/common/locales/en-GB';
import localeEsMX from '@angular/common/locales/es-MX';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import localeZhHans from '@angular/common/locales/zh-Hans';
import {
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    LOCALE_ID,
    provideBrowserGlobalErrorListeners,
    provideZonelessChangeDetection
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService
} from '@azure/msal-angular';
import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '@environments/environment';
import * as Sentry from '@sentry/angular';
import { provideMatomo, withRouter } from 'ngx-matomo-client';
import { withFormAnalytics } from 'ngx-matomo-client/form-analytics';

registerLocaleData( localeDe, 'de' );
registerLocaleData( localeDeAT, 'de-AT' );
registerLocaleData( localeDeCH, 'de-CH' );
registerLocaleData( localeEn, 'en' );
registerLocaleData( localeEnGB, 'en-GB' );
registerLocaleData( localeEsMX, 'es-MX' );
registerLocaleData( localeFr, 'fr' );
registerLocaleData( localeNl, 'nl' );
registerLocaleData( localeZhHans, 'zh-Hans' );

import { MAT_CARD_CONFIG, MatCardConfig } from '@angular/material/card';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { provideOpenCage } from '@digiphilo/opencage-angular';
import { provideMaplibreWorker } from '@maplibre/ngx-maplibre-gl/config';

import { APP_ROUTES } from './app.routes';
import { CustomPaginator } from './components/paginator/customPaginator';
import { msalConfig, msalGuardConfig, msalInterceptorConfig } from './msal.config';
import { LocaleService } from './services/locale.service';
import { ChunkLoadingErrorHandler } from './shared/chunk-error.handler';
import { LoadingInterceptor } from './shared/interceptors/loading.interceptor';
import { TokenInterceptor } from './shared/interceptors/token.interceptor';

/**
 * MSAL instance factory
 * @returns client application
 */
export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication( msalConfig );
}
/**
 * MSAL interceptor factory
 * @returns config
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    return msalInterceptorConfig;
}
/**
 * MSAL guard factory
 * @returns config
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return msalGuardConfig;
}

export function provideMSALConfig() {
    return [
        { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
        { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
        { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory }
    ];
}

export function provideLocale() {
    return [
        {
            provide: LOCALE_ID,
            useFactory: ( localeService: LocaleService ) => localeService.locale,
            deps: [ LocaleService ]
        }
    ];
}

const globalCardConfig: MatCardConfig = {
    appearance: 'outlined'
};

const globalSnackBarConfig: MatSnackBarConfig = {
    horizontalPosition: 'center',
    verticalPosition: 'bottom',
    panelClass: 'riege-snackbar'
};

const globalFormFieldConfig: MatFormFieldDefaultOptions = {
    appearance: 'outline',
    floatLabel: 'always',
    subscriptSizing: 'dynamic'
};

const globalRippleConfig: RippleGlobalOptions = {
    disabled: true, // disable ripple animation globally
    animation: { enterDuration: 0, exitDuration: 0 }
};

const globalDialogConfig: MatDialogConfig = {
    ariaModal: true,
    hasBackdrop: true,
    panelClass: 'riege-dialog'
};

export function provideMaterialConfig() {
    return [
        { provide: MAT_CARD_CONFIG, useValue: globalCardConfig },
        { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: globalDialogConfig },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: globalFormFieldConfig },
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
        { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: globalSnackBarConfig },
        { provide: MatPaginatorIntl, useClass: CustomPaginator }
    ];
}

export function provideSentry() {
    return [
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler( {
                showDialog: false
            } )
        }
    ];
}

export const appConfig: ApplicationConfig = {
    providers: [
        ...provideLocale(),
        importProvidersFrom( BrowserModule ),
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        provideMaplibreWorker( 'maplibre-gl-worker.mjs' ),
        ...provideMaterialConfig(),
        ...provideSentry(),
        provideOpenCage( {
            apiKey: environment.keys.opencage,
            enableCaching: true,
            enableRateLimit: true
        } ),
        { provide: APP_BASE_HREF, useValue: '/' },
        provideRouter(
            APP_ROUTES,
            withInMemoryScrolling( {
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'top'
            } ),
            withComponentInputBinding(),
            withRouterConfig( {
                onSameUrlNavigation: 'reload',
                paramsInheritanceStrategy: 'emptyOnly'
            } )
        ),
        provideHttpClient( withXhr(), withInterceptorsFromDi(), withJsonpSupport() ),
        { provide: ErrorHandler, useClass: ChunkLoadingErrorHandler },
        { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
        provideMatomo(
            {
                acceptDoNotTrack: true,
                disabled: !environment.optionalModules.showCookieConsent,
                requireConsent: 'tracking',
                siteId: environment.keys.matomo,
                trackerUrl: 'https://riege.matomo.cloud'
            },
            withRouter(),
            withFormAnalytics()
        ),
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        ...provideMSALConfig(),
        { provide: Window, useValue: window }
    ]
};
